import { Link } from 'react-router-dom';
import { ArticleData } from '@/mocks/articles';

interface ArticleLayoutProps {
  article: ArticleData;
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  const { title, date, author, category, readTime, introduction, sections, conclusion, faqs, imageUrl } = article;

  return (
    <article className="max-w-3xl mx-auto px-6 py-12 md:py-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": title,
            "datePublished": date,
            "author": {
              "@type": "Organization",
              "name": author
            },
            "publisher": {
              "@type": "Organization",
              "name": "KakobuyFinds",
              "url": import.meta.env.VITE_SITE_URL
            },
            "image": imageUrl,
            "url": `${import.meta.env.VITE_SITE_URL}/${article.slug}`,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `${import.meta.env.VITE_SITE_URL}/${article.slug}`
            },
            "FAQPage": {
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            }
          })
        }}
      />
      <header className="mb-12">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold uppercase">
            {category}
          </span>
          <span className="text-foreground-500 text-xs">{date}</span>
          <span className="text-foreground-400 text-xs">·</span>
          <span className="text-foreground-500 text-xs">{readTime}</span>
        </div>

        <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
          {title}
        </h1>

        <div className="mt-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <i className="ri-user-line text-primary-600 text-lg"></i>
          </div>
          <div>
            <p className="text-foreground-900 text-sm font-semibold">{author}</p>
          </div>
        </div>

        <div className="mt-8 rounded-2xl overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-auto object-cover rounded-2xl"
            loading="lazy"
          />
        </div>
      </header>

      <div className="prose-custom">
        <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-10">
          {introduction}
        </p>

        {sections.map((section, idx) => (
          <section key={idx} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
              {section.heading}
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              {section.content}
            </p>
          </section>
        ))}

        <section className="mb-10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
            Conclusion
          </h2>
          <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
            {conclusion}
          </p>
        </section>

        <section className="mt-16 pt-10 border-t border-background-200">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="flex flex-col gap-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-2">
                  {faq.question}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <nav className="mt-16 pt-8 border-t border-background-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-100 text-foreground-800 font-semibold text-sm hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          Back to Home
        </Link>

        <div className="flex flex-wrap gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Explore Kakobuy Sheet
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
          </a>
          <Link
            to="/#categories"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-300 text-foreground-700 font-semibold text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Browse Categories
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
      </nav>

      <div className="mt-16 p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200 text-center">
        <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">
          Related Articles
        </h3>
        <p className="text-foreground-600 text-sm mb-4">
          Explore more guides and resources to supercharge your shopping experience.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/#guides"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            View All Guides
            <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
          </Link>
          <Link
            to="/#comparisons"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Platform Comparisons
            <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
          </Link>
          <Link
            to="/#safety"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-50 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Safety Tips
            <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}